import { useState, useEffect, useCallback } from 'react';
import { BREAKPOINTS, type BreakpointKey } from '@/config/breakpoints';

export type BreakpointState = Record<BreakpointKey, boolean>;

const queries = Object.entries(BREAKPOINTS).map(
  ([key, px]) => [key as BreakpointKey, `(min-width: ${px}px)`] as const,
);

function getSnapshot(): BreakpointState {
  if (typeof window === 'undefined') {
    return { xs: false, sm: false, md: false, lg: false, xl: false, xxl: false };
  }
  const state = {} as BreakpointState;
  for (const [key, mq] of queries) {
    state[key] = window.matchMedia(mq).matches;
  }
  return state;
}

export function useBreakpoint(): BreakpointState {
  const [state, setState] = useState<BreakpointState>(getSnapshot);

  const refresh = useCallback(() => setState(getSnapshot()), []);

  useEffect(() => {
    const mqls = queries.map(([, mq]) => window.matchMedia(mq));
    for (const mql of mqls) mql.addEventListener('change', refresh);
    refresh();
    return () => {
      for (const mql of mqls) mql.removeEventListener('change', refresh);
    };
  }, [refresh]);

  return state;
}
